/**
 * Locale-aware formatters. Pass the host locale once and reuse the bound
 * fns — Intl formatters are expensive to construct, cheap to call.
 */

export interface Formatters {
  number: (n: number) => string;
  /** Compact form for chart axes / chips, e.g. 1.4k, 12M. */
  compact: (n: number) => string;
  date: (iso: string) => string;
  /** Short month + day, used in chart axes. */
  shortDate: (iso: string) => string;
  range: (fromIso?: string, toIso?: string) => string;
}

export function buildFormatters(locale: string): Formatters {
  const number = new Intl.NumberFormat(locale);
  const compact = new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 });
  const dateFmt = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  const shortDateFmt = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });

  const date = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return dateFmt.format(d);
  };
  const shortDate = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return shortDateFmt.format(d);
  };

  return {
    number: n => number.format(n),
    compact: n => compact.format(n),
    date,
    shortDate,
    range: (fromIso, toIso) => {
      if (!fromIso || !toIso) return '';
      const f = new Date(fromIso);
      const t = new Date(toIso);
      if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return `${fromIso} – ${toIso}`;
      const sameYear = f.getUTCFullYear() === t.getUTCFullYear();
      const fmt = (d: Date) =>
        new Intl.DateTimeFormat(locale, {
          month: 'short',
          day: 'numeric',
          year: sameYear ? undefined : '2-digit',
        }).format(d);
      return `${fmt(f)} – ${fmt(t)}`;
    },
  };
}
